import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Editor from "components/editor";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import {
    Box,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    IconButton
} from "@mui/material";
import { useAppServices } from "hook/services";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const PolicyEditor = () => {
    const AppService = useAppServices();

    const tabKeys = ["terms", "privacy", "website", "hosts", "creatives", "siteIssuse", "contactUs"];
    const tabLabels = ["T & C’s", "Privacy Policy", "Website T & C’s", "Hosts", "Creatives", "Site Issues", "Contact Us"];

    const NESTED_KEYS = ["hosts", "creatives", "siteIssuse", "contactUs"];

    const [activeTab, setActiveTab] = useState(0);
    const [activeSubTab, setActiveSubTab] = useState(null);
    const [contents, setContents] = useState({
        terms: "",
        privacy: "",
        website: "",
        hosts: { parent: "", children: [] },
        creatives: { parent: "", children: [] },
        siteIssuse: { parent: "", children: [] },
        contactUs: { parent: "", children: [] }
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [newTabName, setNewTabName] = useState("");

    const currentKey = tabKeys[activeTab];
    const isCurrentTabNested = () => NESTED_KEYS.includes(currentKey);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const { response } = await AppService.content.get();
            if (response.success && response.data) {
                const data = response.data || {};
                const parseNested = (val) => (Array.isArray(val) ? val : []);

                const getNested = (key) => {
                    const val = data[key] || {};
                    return {
                        parent: val?.parent || "",
                        children: parseNested(val?.children)
                    };
                };

                setContents({
                    terms: data.terms || "",
                    privacy: data.privacy || "",
                    website: data.website || "",
                    hosts: getNested("hosts"),
                    creatives: getNested("creatives"),
                    siteIssuse: getNested("siteIssuse"),
                    contactUs: getNested("contactUs"),
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => { fetchContent(); }, []);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setActiveSubTab(null);
    };

    const handleSubTabChange = (event, newValue) => setActiveSubTab(newValue);

    const handleEditorChange = (value) => {
        if (isCurrentTabNested()) {
            if (activeSubTab === null) {
                setContents(prev => ({ ...prev, [currentKey]: { ...prev[currentKey], parent: value } }));
            } else {
                setContents(prev => {
                    const updatedChildren = [...prev[currentKey].children];
                    updatedChildren[activeSubTab] = { ...updatedChildren[activeSubTab], content: value };
                    return { ...prev, [currentKey]: { ...prev[currentKey], children: updatedChildren } };
                });
            }
        } else {
            setContents(prev => ({ ...prev, [currentKey]: value }));
        }
    };

    const handleAddNewTab = () => {
        if (!newTabName.trim()) return;
        setContents(prev => {
            const updatedChildren = [...prev[currentKey].children, { title: newTabName, content: "" }];
            return { ...prev, [currentKey]: { ...prev[currentKey], children: updatedChildren } };
        });
        setActiveSubTab(contents[currentKey].children.length);
        setNewTabName("");
        setOpenModal(false);
    };

    const handleDeleteSubTab = (index) => {
        setContents(prev => {
            const updatedChildren = prev[currentKey].children.filter((_, i) => i !== index);
            return { ...prev, [currentKey]: { ...prev[currentKey], children: updatedChildren } };
        });
        setActiveSubTab(null);
    };

    const handleSave = async () => {
        setSaving(true);
        const payload = isCurrentTabNested()
            ? {
                key: currentKey,
                value: {
                    parent: contents[currentKey].parent,
                    children: contents[currentKey].children
                }
            }
            : { key: currentKey, value: contents[currentKey] };

        try {
            const response = await AppService.content.update({ payload });
            await fetchContent();
            if (response) alert("Content updated successfully!");

            else alert(response.message || "Error saving content");
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const getCurrentData = () => {
        if (isCurrentTabNested()) {
            if (activeSubTab === null) return contents[currentKey]?.parent || "";
            return contents[currentKey]?.children?.[activeSubTab]?.content || "";
        }
        return contents[currentKey] || "";
    };


    if (loading) return <DashboardLayout><Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box></DashboardLayout>;

    const subTabs = isCurrentTabNested() ? contents[currentKey].children : [];

    return (
        <DashboardLayout>
            <Box sx={{ p: 4 }}>
                <Tabs value={activeTab} onChange={handleTabChange} textColor="primary" indicatorColor="primary" sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                    {tabLabels.map((label, index) => <Tab key={index} label={label} />)}
                </Tabs>
                {isCurrentTabNested() && (
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        {subTabs.length > 0 && (
                            <Box sx={{ flex: 1, overflowX: 'auto' }}>
                                <Tabs
                                    value={activeSubTab === null ? false : activeSubTab}
                                    onChange={handleSubTabChange}
                                    textColor="secondary"
                                    indicatorColor="secondary"
                                    variant="scrollable"
                                    scrollButtons="desktop" 
                                    allowScrollButtonsMobile 
                                    sx={{ minWidth: 'max-content' }}
                                >
                                    {subTabs.map((item, index) => (
                                        <Tab
                                            key={index}
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.title || `Tab ${index + 1}`}
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteSubTab(index); }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(`https://aloca.com.au/help-centre/${currentKey}/${item._id || ""}`);
                                                        }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            }
                                            sx={{ width: 200, minWidth: 200, maxWidth: 200, marginRight: 2 }}
                                        />
                                    ))}
                                </Tabs>
                            </Box>
                        )}
                        <Button
                            startIcon={<AddIcon />}
                            size="small"
                            variant="contained" color="primary"
                            onClick={() => setOpenModal(true)}
                            sx={{ minWidth: 'fit-content', color: "white" }}
                            style={{ color: "white" }}
                        >
                            Add Nested Page
                        </Button>
                    </Box>
                )}
                <Editor
                    key={`${currentKey}-${activeSubTab === null ? 'parent' : activeSubTab}`}
                    data={getCurrentData()}
                    handleChange={handleEditorChange}
                />
                <Box sx={{ mt: 3 }}>
                    <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} style={{ color: "white" }}
                    >
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </Box>
                <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { minHeight: 250, padding: 2 } }}>
                    <DialogTitle>Add New Page - {tabLabels[activeTab]}</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Page Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newTabName}
                            onChange={(e) => setNewTabName(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenModal(false)}>Cancel</Button>
                        <Button onClick={handleAddNewTab} variant="contained" color="primary" disabled={!newTabName.trim()} sx={{ color: 'white' }}>
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </DashboardLayout>
    );
};

export default PolicyEditor;
